import { countBy, debounce } from 'lodash-es';
import { __ } from '~/locale';
import { getBaseURL, isValidURL, relativePathToAbsolute, visitUrl } from '~/lib/utils/url_utility';
import { sandboxMermaidV12Path } from '~/lib/utils/path_helpers/routes';
import { darkModeEnabled } from '~/lib/utils/color_utils';
import { setAttributes, isElementVisible } from '~/lib/utils/dom_utils';
import { createAlert, VARIANT_WARNING } from '~/alert';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { PanelBreakpointInstance } from '~/panel_breakpoint_instance';
import { pageSingleton } from '~/lib/utils/page_singleton';
import { unrestrictedPages } from './constants';
import { getRenderedMermaidBlocks, incrementRenderedMermaidBlocks } from './mermaid_render_count';

// Renders diagrams and flowcharts from text using Mermaid in any element with the
// `js-render-mermaid` class.
//
// Example markup:
//
// <pre class="js-render-mermaid">
//  graph TD;
//    A-- > B;
//    A-- > C;
//    B-- > D;
//    C-- > D;
// </pre>
//

// This is an arbitrary number; Can be iterated upon when suitable.
export const MAX_CHAR_LIMIT = 2000;

// Max # of mermaid blocks that can be rendered in a page.
export const MAX_MERMAID_BLOCK_LIMIT = 50;

// Max # of `&` allowed in Chaining of links syntax
const MAX_CHAINING_OF_LINKS_LIMIT = 30;

export const BUFFER_IFRAME_HEIGHT = 10;
export const SANDBOX_ATTRIBUTES = 'allow-scripts';

// Messages other than the height payload can also arrive
// from the sandboxed iframe (such as those injected by Chrome for iOS).
export function getIframeHeightFromMessage(data) {
  const h = data?.h;
  return Number.isFinite(h) ? h + BUFFER_IFRAME_HEIGHT : null;
}

// Link clicks inside the sandboxed iframe are delegated to the parent
// because links can't open from within the sandbox. Validate the URL here,
// outside the reach of the diagram source, before opening it.
export function openLinkFromMessage(data) {
  const href = data?.href;
  // visitUrl re-validates the URL, but throws;
  // check first so non-link messages are silently ignored.
  if (typeof href !== 'string' || !isValidURL(href)) {
    return false;
  }

  visitUrl(href, true);
  return true;
}

const ALERT_CONTAINER_CLASS = 'mermaid-alert-container';
export const LAZY_ALERT_SHOWN_CLASS = 'lazy-alert-shown';

// Keep a map of mermaid blocks we've already rendered.
const elsProcessingMap = pageSingleton('render_sandboxed_mermaid_els', () => new WeakMap());

/**
 * Determines whether a given Mermaid diagram is visible.
 *
 * @param {Element} el The Mermaid DOM node
 * @returns
 */
const isVisibleMermaid = (el) => el.closest('details') === null && isElementVisible(el);

function shouldLazyLoadMermaidBlock(source) {
  /**
   * If source contains `&`, which means that it might
   * contain Chaining of links a new syntax in Mermaid.
   */
  if (countBy(source)['&'] > MAX_CHAINING_OF_LINKS_LIMIT) {
    return true;
  }

  return false;
}

function fixElementSource(el) {
  // Mermaid doesn't like `<br />` tags, so collapse all like tags into `<br>`, which is parsed correctly.
  const source = el.textContent?.replace(/<br\s*\/>/g, '<br>');

  return { source };
}

export function getSandboxFrameSrc() {
  const sandboxPath = sandboxMermaidV12Path;
  const relativeURL = darkModeEnabled() ? sandboxPath({ darkMode: true }) : sandboxPath();

  return relativePathToAbsolute(relativeURL, getBaseURL());
}

function renderMermaidEl(el, source) {
  // Added in MermaidFilter if the asset proxy is enabled.
  const proxiedUrls = el.dataset.proxiedUrls ? JSON.parse(el.dataset.proxiedUrls) : null;

  const pre = el.closest('pre');
  const wrapper = pre?.closest('.js-markdown-code') || pre?.parentNode;

  if (!wrapper) return;

  const iframeEl = document.createElement('iframe');
  setAttributes(iframeEl, {
    src: getSandboxFrameSrc(),
    sandbox: SANDBOX_ATTRIBUTES,
    frameBorder: 0,
    scrolling: 'no',
    width: '100%',
  });

  const iframeContainer = document.createElement('div');
  iframeContainer.appendChild(iframeEl);

  // Ensure all mermaid diagrams renders as block.
  // By default Duo UI renders markdown with flex which causes layout issues.
  if (wrapper.classList.contains('js-markdown-code')) {
    wrapper.classList.add('!gl-block');
  }

  // Hide the pre but keep it "visible enough" to allow Copy-as-GFM
  // https://gitlab.com/gitlab-org/gitlab/-/merge_requests/83202
  // Also remove padding from the pre element to prevent errant scrollbar appearing
  // and hide the border for duo-ui pre element
  pre.classList.add('gl-sr-only', '!gl-p-0', '!gl-border-none');
  wrapper.appendChild(iframeContainer);

  // Function to request render/re-render from sandbox
  let hasRendered = false;
  const renderSandbox = () => {
    // Potential risk associated with '*' discussed in below thread
    // https://gitlab.com/gitlab-org/gitlab/-/merge_requests/74414#note_735183398
    iframeEl.contentWindow?.postMessage(
      // eslint-disable-next-line @gitlab/no-hardcoded-urls
      { source, proxiedUrls, relativeRootPath: window.gon?.relative_url_root || null },
      '*',
    );
    hasRendered = true;
  };

  // Event Listeners
  iframeEl.addEventListener('load', renderSandbox);

  window.addEventListener(
    'message',
    (event) => {
      if (event.origin !== 'null' || event.source !== iframeEl.contentWindow) {
        return;
      }
      if (openLinkFromMessage(event.data)) {
        return;
      }
      const height = getIframeHeightFromMessage(event.data);
      if (height === null) {
        return;
      }
      iframeEl.height = `${height}px`;
    },
    false,
  );

  // Re-render diagram when panel resizes to recalculate height
  const debouncedResize = debounce(() => {
    if (!document.contains(iframeContainer)) {
      PanelBreakpointInstance.removeResizeListener(debouncedResize);
      return;
    }
    if (hasRendered) {
      renderSandbox();
    }
  }, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);

  PanelBreakpointInstance.addResizeListener(debouncedResize);
}

function renderMermaids(els) {
  if (!els.length) return;

  const pageName = document.querySelector('body').dataset.page;

  // A diagram may have been truncated in search results which will cause errors, so abort the render.
  if (pageName === 'search:show') return;

  let renderedChars = 0;

  els.forEach((el) => {
    // Skipping all the elements which we've already queued in requestIdleCallback
    if (elsProcessingMap.has(el)) {
      return;
    }

    const { source } = fixElementSource(el);
    /**
     * Restrict the rendering to a certain amount of character
     * and mermaid blocks to prevent mermaidjs from hanging
     * up the entire thread and causing a DoS.
     */
    if (
      !unrestrictedPages.includes(pageName) &&
      ((source && source.length > MAX_CHAR_LIMIT) ||
        renderedChars > MAX_CHAR_LIMIT ||
        getRenderedMermaidBlocks() >= MAX_MERMAID_BLOCK_LIMIT ||
        shouldLazyLoadMermaidBlock(source))
    ) {
      const parent = el.parentNode;

      if (!parent.classList.contains(LAZY_ALERT_SHOWN_CLASS)) {
        const alertContainer = document.createElement('div');
        alertContainer.classList.add(ALERT_CONTAINER_CLASS, 'gl-mb-5');
        const noteText = parent.closest('.note-text');
        const markdownBlock = noteText?.querySelector('.markdown-code-block') || parent;
        markdownBlock.before(alertContainer);
        createAlert({
          message: __(
            'Warning: Displaying this diagram might cause performance issues on this page.',
          ),
          variant: VARIANT_WARNING,
          parent: noteText || parent.parentNode,
          containerSelector: `.${ALERT_CONTAINER_CLASS}`,
          primaryButton: {
            text: __('Display'),
            clickHandler: () => {
              alertContainer.remove();
              renderMermaidEl(el, source);
            },
          },
        });
        parent.classList.add(LAZY_ALERT_SHOWN_CLASS);
      }

      return;
    }

    renderedChars += source.length;
    incrementRenderedMermaidBlocks();

    const requestId = window.requestIdleCallback(() => {
      renderMermaidEl(el, source);
    });

    elsProcessingMap.set(el, requestId);
  });
}

export default function renderMermaid(els) {
  if (!els.length) return;

  const visibleMermaids = [];
  const hiddenMermaids = [];

  for (const el of els) {
    if (isVisibleMermaid(el)) {
      visibleMermaids.push(el);
    } else {
      hiddenMermaids.push(el);
    }
  }

  renderMermaids(visibleMermaids);

  if (hiddenMermaids.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        visible.forEach((entry) => observer.unobserve(entry.target));
        renderMermaids(visible.map((entry) => entry.target));
      },
      { threshold: 0 },
    );
    hiddenMermaids.forEach((el) => observer.observe(el));
  }
}
